import { CouponRow, PriceRow } from 'server/types';
import { CampaignSection, CheckoutSection } from 'server/types/pmts-overrides';
import {
  PurchaseFn,
  StripeHandlerFn,
  PriceSummary,
  AipBackupOfferDisplay,
} from 'shared/payments/types';
import { CampaignHero } from './campaign-hero';
import { ContentSection } from './content-section';
import { UrgencyBar } from './urgency-bar';
import { BenefitsList } from './benefits-list';
import { IntegratedCheckout } from './integrated-checkout';
import { FAQAccordion as CampaignFAQ } from './faq-accordion';
import { InstructorBio } from './instructor-bio';
import { CampaignFooter } from './campaign-footer';
import { VideoEmbed } from './video-embed';
import { TrustBar } from './trust-bar';
import { FeatureGrid } from './feature-grid';
import { NarrativeCopy } from './narrative-copy';
import { TestimonialsSidebar } from './testimonials-sidebar';
import { OfferCard } from './offer-card';
import { ScheduleCards } from './schedule-cards';
import { EyebrowText } from './eyebrow-text';
import { CalloutBlock } from './callout-block';
import { UpsellCTA } from '../sales-checkout/upsell-cta';
import { StripeAddress } from 'shared/payments/types';
import { TwoColumnSection } from '../sales-checkout/two-column-section';
import { CheckoutFormWizard } from '../sales-checkout/checkout-form-wizard';

type CheckoutProps = {
  courseId: string;
  price: PriceRow;
  coupon?: CouponRow;
  collectAddress: boolean;
  handleStripe?: StripeHandlerFn;
  beginPurchase: PurchaseFn;
  onPurchaseComplete(opts?: { newUser?: boolean }): Promise<unknown>;
  alternativePrices?: PriceSummary[];
  campaignSlug?: string;
  testModeToken?: string;
  aipBackupOffer?: AipBackupOfferDisplay;
  simpleCheckConsent?: boolean;
};

type UpsellProps = {
  onAccept(paymentMethodId?: string, address?: StripeAddress): void;
  onDecline(): void;
  isProcessing: boolean;
  cardInfo?: { last4: string; brand: string };
  priceId?: string;
  trialDays?: number;
  priceDisplay?: string;
  amount?: number;
  currency?: string;
  paymentType?: string;
};

type Props = {
  sections: (CampaignSection | CheckoutSection)[];
  checkoutProps?: CheckoutProps;
  upsellProps?: UpsellProps;
  brand?: string;
};

function isCampaignSection(section: CampaignSection | CheckoutSection): section is CampaignSection {
  const type = section.type;
  if (
    type === 'valueStack' ||
    type === 'featureGrid' ||
    type === 'testimonials' ||
    type === 'video' ||
    type === 'trust' ||
    type === 'cta' ||
    type === 'checkoutForm' ||
    type === 'integratedCheckout' ||
    type === 'schedule' ||
    type === 'eyebrow' ||
    type === 'callout'
  ) {
    return false;
  }
  if (type === 'benefits' || type === 'checkout') {
    return true;
  }
  return !('content' in section);
}

function UpsellCTAPreview({
  acceptText,
  declineText,
}: {
  acceptText: string;
  declineText: string;
}) {
  return (
    <div class="text-center">
      <button
        type="button"
        class="font-bold py-4 px-10 rounded-lg text-lg"
        style="background: linear-gradient(135deg, var(--m-orange-cta-start, #ff6b00) 0%, var(--m-orange-cta-end, #ffc600) 100%); color: var(--m-text-dark, #1a1a1a); box-shadow: 0 2px 8px rgba(255,107,0,0.3)"
        disabled
      >
        {acceptText}
      </button>
      <button type="button" class="block mx-auto mt-4 text-gray-500 text-sm underline" disabled>
        {declineText}
      </button>
    </div>
  );
}

function findCheckoutSection(
  sections: (CampaignSection | CheckoutSection)[],
  type: string,
): CheckoutSection | undefined {
  return sections.find((s) => 'content' in s && s.type === type) as CheckoutSection | undefined;
}

function renderCampaignSection(
  section: CampaignSection,
  checkoutProps: CheckoutProps | undefined,
  i: number,
  brand?: string,
) {
  switch (section.type) {
    case 'hero':
      return <CampaignHero key={i} {...section} brand={brand} />;
    case 'content':
      return <ContentSection key={i} {...section} />;
    case 'urgency':
      return <UrgencyBar key={i} {...section} />;
    case 'benefits':
      return <BenefitsList key={i} {...section} />;
    case 'checkout':
      if (!checkoutProps) {
        return undefined;
      }
      return <IntegratedCheckout key={i} {...checkoutProps} {...section} />;
    case 'faq':
      return <CampaignFAQ key={i} {...section} />;
    case 'instructor':
      return <InstructorBio key={i} {...section} />;
    case 'footer':
      return <CampaignFooter key={i} {...section} brand={brand} />;
    default:
      return undefined;
  }
}

function renderCheckoutSection(
  section: CheckoutSection,
  allSections: (CampaignSection | CheckoutSection)[],
  checkoutProps: CheckoutProps | undefined,
  upsellProps: UpsellProps | undefined,
  i: number,
  brand?: string,
) {
  switch (section.type) {
    case 'hero':
      return (
        <CampaignHero
          key={i}
          headline={section.content.headline}
          subtitle={section.content.subtitle}
          subtitle2={section.content.subtitle2}
          subtitleColor={section.content.subtitleColor}
          logoUrl={section.content.logoUrl}
          badge={section.content.priceBadgeText}
          gradientFrom={section.settings?.gradientFrom}
          gradientTo={section.settings?.gradientTo}
          brand={brand}
        >
          {section.content.ctaText && (
            <a
              href={section.content.ctaUrl || '#checkout'}
              class="campaign-hero__cta"
              onClick={(e) => {
                const target = section.content.ctaUrl || '#checkout';
                if (target.startsWith('#')) {
                  e.preventDefault();
                  const el = document.querySelector(target);
                  if (el) {
                    el.scrollIntoView({ behavior: 'smooth' });
                  }
                }
              }}
            >
              {section.content.ctaText}
            </a>
          )}
        </CampaignHero>
      );

    case 'valueStack':
      return (
        <BenefitsList key={i} heading={section.content.heading} items={section.content.items} />
      );

    case 'featureGrid':
      return (
        <FeatureGrid
          key={i}
          label={section.content.label}
          heading={section.content.heading}
          columns={section.settings?.columns}
          items={section.content.items}
        />
      );

    case 'video':
      return <VideoEmbed key={i} url={section.content.url} caption={section.content.caption} />;

    case 'content': {
      const blocks = section.content.blocks;
      if (blocks.length === 1 && blocks[0].type === 'html' && blocks[0].html) {
        return <ContentSection key={i} html={blocks[0].html} />;
      }
      return <NarrativeCopy key={i} blocks={blocks} maxWidth={section.settings?.maxWidth} />;
    }

    case 'trust':
      return (
        <TrustBar
          key={i}
          badges={section.content.badges}
          guaranteeText={section.content.guaranteeText}
          phone={section.content.phone}
          altPaymentText={section.content.altPaymentText}
        />
      );

    case 'faq':
      return (
        <CampaignFAQ key={i} heading={section.content.heading} items={section.content.items} />
      );

    case 'testimonials':
      if (section.settings?.variant === 'sidebar') {
        return undefined;
      }
      return (
        <div key={i} class="max-w-3xl mx-auto px-6 py-8">
          <TestimonialsSidebar items={section.content.items} />
        </div>
      );

    case 'cta': {
      const acceptText = section.content.acceptText || 'Yes, add this!';
      const declineText = section.content.declineText || 'No thanks, continue';
      if (!upsellProps) {
        return (
          <OfferCard key={i} id="checkout">
            <UpsellCTAPreview acceptText={acceptText} declineText={declineText} />
          </OfferCard>
        );
      }
      return (
        <OfferCard key={i} id="checkout">
          <UpsellCTA
            acceptText={acceptText}
            declineText={declineText}
            onAccept={upsellProps.onAccept}
            onDecline={upsellProps.onDecline}
            isProcessing={upsellProps.isProcessing}
            cardInfo={upsellProps.cardInfo}
            priceId={upsellProps.priceId}
            trialDays={upsellProps.trialDays}
            priceDisplay={upsellProps.priceDisplay}
            amount={upsellProps.amount}
            currency={upsellProps.currency}
            paymentType={upsellProps.paymentType}
          />
        </OfferCard>
      );
    }

    case 'checkoutForm': {
      if (!checkoutProps) {
        return undefined;
      }
      const testimonialSection = findCheckoutSection(allSections, 'testimonials');
      const hasSidebar =
        section.settings?.layout === 'with-sidebar' && testimonialSection?.type === 'testimonials';

      const formComponent = (
        <CheckoutFormWizard
          courseId={checkoutProps.courseId}
          price={checkoutProps.price}
          coupon={checkoutProps.coupon}
          collectAddress={checkoutProps.collectAddress}
          handleStripe={checkoutProps.handleStripe}
          beginPurchase={checkoutProps.beginPurchase}
          onPurchaseComplete={checkoutProps.onPurchaseComplete}
          simpleCheckConsent={checkoutProps.simpleCheckConsent}
        />
      );

      if (hasSidebar && testimonialSection.type === 'testimonials') {
        return (
          <TwoColumnSection
            key={i}
            main={formComponent}
            sidebar={<TestimonialsSidebar items={testimonialSection.content.items} />}
          />
        );
      }

      return (
        <div key={i} id="checkout" class="max-w-xl mx-auto px-6 py-8">
          {formComponent}
        </div>
      );
    }

    case 'instructor':
      return (
        <InstructorBio
          key={i}
          name={section.content.name}
          title={section.content.title}
          photoUrl={section.content.photoUrl}
          bio={section.content.bio}
        />
      );

    case 'urgency':
      return <UrgencyBar key={i} text={section.content.text} />;

    case 'footer':
      return (
        <CampaignFooter
          key={i}
          logoUrl={section.content.logoUrl}
          disclaimerHtml={section.content.disclaimerHtml}
          contactPhone={section.content.contactPhone}
          contactEmail={section.content.contactEmail}
          termsHtml={section.content.termsHtml}
          brand={brand}
        />
      );

    case 'integratedCheckout':
      if (!checkoutProps) {
        return undefined;
      }
      return (
        <IntegratedCheckout
          key={i}
          {...checkoutProps}
          pricingNote={section.content.pricingNote}
          deadlineText={section.content.deadlineText}
          contactPhone={section.content.contactPhone}
          contactEmail={section.content.contactEmail}
          ctaText={section.content.ctaText}
          testimonials={section.content.testimonials}
        />
      );

    case 'schedule':
      return (
        <ScheduleCards key={i} heading={section.content.heading} items={section.content.items} />
      );

    case 'eyebrow':
      return <EyebrowText key={i} text={section.content.text} />;

    case 'callout':
      return (
        <CalloutBlock
          key={i}
          icon={section.content.icon}
          heading={section.content.heading}
          description={section.content.description}
        />
      );

    default:
      return undefined;
  }
}

export function SectionRenderer({ sections, checkoutProps, upsellProps, brand }: Props) {
  return (
    <>
      {sections.map((section, i) => {
        if (isCampaignSection(section)) {
          return renderCampaignSection(section, checkoutProps, i, brand);
        }
        return renderCheckoutSection(
          section as CheckoutSection,
          sections,
          checkoutProps,
          upsellProps,
          i,
          brand,
        );
      })}
    </>
  );
}
